import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const STATUS = {
  pending: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  invited: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  accepted: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  declined: "text-slate-400 border-slate-600 bg-slate-500/10",
};

export const AccessRequestsTab = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [links, setLinks] = useState({});
  const load = useCallback(() => api.get("/admin/access-requests").then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);

  const invite = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/admin/access-requests/${r.id}/invite`);
      setLinks((l) => ({ ...l, [r.id]: data.invite_link }));
      data.email_sent ? toast.success(`Invitation emailed to ${r.email}`) : toast.error("Email failed. Copy the link and send it yourself.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };
  const decline = async (r) => {
    try { await api.post(`/admin/access-requests/${r.id}/decline`); toast.success("Request declined"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const copy = (id) => navigator.clipboard.writeText(links[id]).then(() => toast.success("Invitation link copied"));

  const pending = items.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-4">
      <p data-testid="requests-pending-count" className="text-sm text-slate-400">
        {pending} pending request{pending === 1 ? "" : "s"}. Invitations are emailed with a one-time link that expires in 7 days.
      </p>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-[#131927]">
            <tr className="text-left">{["Requester", "Organization", "Requested", "Status", ""].map((h) => <th key={h} className="eyebrow px-5 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {items.map((r) => (
              <tr key={r.id} data-testid={`request-row-${r.id}`}>
                <td className="px-5 py-4"><div className="font-semibold">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></td>
                <td className="px-5 py-4 text-slate-300">{r.organization}</td>
                <td className="px-5 py-4 text-slate-400">{fmtDate(r.created_at)}</td>
                <td className="px-5 py-4">
                  <span data-testid={`request-status-${r.id}`} className={`rounded-full border px-2.5 py-0.5 font-data text-[11px] uppercase ${STATUS[r.status]}`}>{r.status}</span>
                  {r.status === "invited" && <div className="mt-1 font-data text-[11px] text-slate-500">Expires {fmtDate(r.invite_expires_at)}</div>}
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    {links[r.id] && r.status === "invited" && (
                      <button data-testid={`request-copy-link-${r.id}`} onClick={() => copy(r.id)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs hover:border-white">
                        <Copy className="h-3.5 w-3.5" /> Copy link
                      </button>
                    )}
                    {r.status !== "accepted" && (
                      <button data-testid={`request-invite-${r.id}`} onClick={() => invite(r)} disabled={busy === r.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#FF2D78] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#E01F66] disabled:opacity-60">
                        {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                        {r.status === "invited" ? "Resend invite" : "Send invite"}
                      </button>
                    )}
                    {r.status === "pending" && (
                      <button data-testid={`request-decline-${r.id}`} onClick={() => decline(r)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-red-400 hover:text-red-300">
                        <X className="h-3.5 w-3.5" /> Decline
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <p data-testid="requests-empty" className="p-10 text-center text-sm text-slate-500">No access requests yet.</p>}
      </div>
    </div>
  );
};
